import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Sparkles, ShieldCheck, Layers, MessageSquare, Home as HomeIcon, Wrench, Briefcase, Ticket, Car, Plane, UtensilsCrossed, Scale } from "lucide-react";
import { useLang } from "../context/LanguageContext";
import { IMAGES } from "../data/demo";
import { StatusPill, Eyebrow } from "../components/Bits";
import { HomeDiscover } from "../components/HomeDiscover";

const CAT_ICONS = { property: HomeIcon, services: Wrench, business: Briefcase, experiences: Ticket, cars: Car, relocation: Plane, dining: UtensilsCrossed, professional: Scale };

export default function Home() {
  const { t } = useLang();
  const nav = useNavigate();
  const [q, setQ] = useState("");
  const [idx, setIdx] = useState(0);
  const prompts = t("home.prompts");

  useEffect(() => {
    const id = setInterval(() => setIdx((i) => (i + 1) % prompts.length), 3000);
    return () => clearInterval(id);
  }, [prompts.length]);

  const go = (text) => nav("/ask", { state: { prompt: text || q } });

  return (
    <div data-testid="home-page">
      <section className="relative min-h-[88vh] flex flex-col justify-center overflow-hidden py-28">
        <div className="absolute inset-0">
          <img src={IMAGES.heroSkyline} alt="Dubai skyline" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-ink/80" />
          <div className="absolute inset-0 bg-gradient-to-b from-ink/70 via-ink/60 to-ink" />
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_center,transparent_0%,rgba(10,15,36,0.7)_70%)]" />
        </div>
        <div className="absolute top-1/3 left-1/2 -translate-x-1/2 w-[600px] h-[600px] gold-glow animate-pulse-glow pointer-events-none" />

        <div className="relative z-10 max-w-4xl mx-auto px-5 text-center">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}
            className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.25em] text-gold/80 mb-6">
            <Sparkles size={14} /> {t("home.badge")}
          </motion.div>
          <motion.h1 initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, delay: 0.1 }}
            className="font-display text-5xl sm:text-6xl lg:text-7xl font-light tracking-tight text-white leading-[1.05]">
            {t("home.h1a")}<br />{t("home.h1b")}
          </motion.h1>
          <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.3 }}
            className="mt-6 text-base sm:text-lg text-white/70 max-w-2xl mx-auto leading-relaxed">
            {t("home.sub")}
          </motion.p>

          <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.45 }} className="mt-10 max-w-2xl mx-auto">
            <div className="glass rounded-2xl p-2 flex items-center gap-2 shadow-[0_8px_40px_rgba(0,0,0,0.5)] focus-within:border-gold/40 transition-colors duration-300">
              <input data-testid="ask-dubai-input" value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.key === "Enter" && go()}
                placeholder={t("home.placeholder")} className="flex-1 bg-transparent px-4 py-3 text-white placeholder:text-white/40 outline-none text-sm sm:text-base" />
              <button onClick={() => go()} data-testid="submit-prompt-btn" aria-label="Ask BuyDubai AI"
                className="shrink-0 w-11 h-11 rounded-xl bg-gold text-ink flex items-center justify-center hover:brightness-110 transition-all duration-200"><ArrowRight size={20} /></button>
            </div>
            <button onClick={() => go(prompts[idx])} data-testid="rotating-prompt" className="mt-4 text-sm text-white/45 hover:text-gold transition-colors duration-300 min-h-[20px]">“{prompts[idx]}”</button>
            <div className="mt-3 text-xs text-emerald-400/90" data-testid="hero-live-line">{t("home.live_line")}</div>
            <div className="mt-6 flex flex-wrap justify-center gap-2">
              {t("home.prompts_soon").map((s) => (
                <span key={s.text} className="text-[11px] px-3 py-1 rounded-full bg-white/[0.04] border border-white/10 text-white/45">
                  {s.text} <span className="text-gold/70">· {s.tag}</span>
                </span>
              ))}
            </div>
          </motion.div>

          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.6 }} className="mt-8 flex flex-col sm:flex-row items-center justify-center gap-3">
            <button onClick={() => go()} data-testid="cta-ask" className="px-7 py-3 rounded-full bg-gold text-ink font-semibold hover:brightness-110 transition-all duration-200">{t("common.ask_ai")}</button>
            <button onClick={() => nav("/partners")} data-testid="cta-partner" className="px-7 py-3 rounded-full border border-white/15 text-white hover:border-gold/40 transition-colors duration-200">{t("common.become_partner")}</button>
          </motion.div>
        </div>
      </section>

      <section className="max-w-[1400px] mx-auto px-5 lg:px-8 mt-6 relative z-20">
        <div className="glass rounded-2xl p-8 grid md:grid-cols-3 gap-6">
          {t("home.five").map((f, i) => {
            const Icon = [MessageSquare, Layers, ShieldCheck][i];
            return (
              <div key={i} className="flex gap-3">
                <div className="w-9 h-9 rounded-lg bg-gold/10 text-gold flex items-center justify-center shrink-0"><Icon size={18} /></div>
                <div><div className="text-white font-semibold text-sm">{f.h}</div><div className="text-white/55 text-sm mt-1">{f.p}</div></div>
              </div>
            );
          })}
        </div>
      </section>

      <HomeDiscover />

      <section className="max-w-[1400px] mx-auto px-5 lg:px-8 mt-24">
        <Eyebrow>{t("common.ask_ai")}</Eyebrow>
        <h2 className="font-display text-3xl sm:text-4xl text-white mt-2">{t("home.cap_h")}</h2>
        <p className="text-white/55 mt-2 max-w-2xl">{t("home.cap_sub")}</p>
        <div className="grid grid-cols-1 md:grid-cols-12 gap-4 mt-8">
          <div className="md:col-span-7 relative rounded-2xl overflow-hidden min-h-[280px] group" data-testid="capability-property">
            <img src={IMAGES.business} alt="Dubai property" loading="lazy" className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
            <div className="absolute inset-0 bg-gradient-to-t from-ink via-ink/50 to-transparent" />
            <div className="absolute bottom-0 p-7">
              <StatusPill status="live" />
              <h3 className="font-display text-2xl text-white mt-3">{t("nav.property")}</h3>
              <p className="text-white/70 text-sm mt-1 max-w-md">{t("home.property_desc")}</p>
            </div>
          </div>
          <div className="md:col-span-5 grid gap-4">
            {t("home.coming").map((c) => (
              <div key={c.h} className="rounded-2xl bg-surface/60 border border-white/[0.06] p-6 hover:border-gold/30 transition-colors duration-300">
                <StatusPill status="coming" />
                <h3 className="font-display text-xl text-white mt-2">{c.h}</h3>
                <p className="text-white/55 text-sm mt-1">{c.p}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="max-w-[1400px] mx-auto px-5 lg:px-8 mt-24">
        <h2 className="font-display text-3xl sm:text-4xl text-white mb-8">{t("home.explore_h")}</h2>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {t("home.cats").map((c) => {
            const Icon = CAT_ICONS[c.key] || Sparkles;
            return (
              <button key={c.key} onClick={() => nav("/explore")} data-testid={`home-cat-${c.key}`}
                className="text-left rounded-2xl bg-surface/60 border border-white/[0.06] p-6 hover:border-gold/30 hover:-translate-y-1 transition-all duration-300">
                <Icon size={20} className="text-gold" />
                <div className="font-display text-lg text-white mt-3">{c.name}</div>
              </button>
            );
          })}
        </div>
      </section>

      <section className="max-w-[1400px] mx-auto px-5 lg:px-8 mt-24">
        <div className="glass rounded-3xl p-8 md:p-12 grid md:grid-cols-2 gap-8 items-center" data-testid="home-broker-section">
          <div>
            <div className="text-xs uppercase tracking-[0.2em] text-gold/80">{t("home.broker.eyebrow")}</div>
            <h2 className="font-display text-3xl sm:text-4xl text-white mt-2">{t("home.broker.h")}</h2>
            <p className="text-white/60 mt-3">{t("home.broker.p")}</p>
            <button onClick={() => nav("/partners/broker")} data-testid="home-broker-cta"
              className="mt-6 inline-flex items-center gap-1.5 px-6 py-3 rounded-full bg-gold text-ink font-semibold hover:brightness-110 transition-all duration-200">
              {t("home.broker.cta")} <ArrowRight size={16} />
            </button>
          </div>
          <ul className="grid gap-3">
            {t("home.broker.bullets").map((b) => (
              <li key={b} className="flex items-center gap-2.5 text-sm text-white/75 rounded-xl bg-white/[0.03] border border-white/5 px-4 py-3">
                <ShieldCheck size={15} className="text-gold shrink-0" /> {b}
              </li>
            ))}
          </ul>
        </div>
      </section>

      <section className="max-w-[1400px] mx-auto px-5 lg:px-8 mt-24">
        <div className="glass rounded-3xl p-12 text-center relative overflow-hidden">
          <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[400px] h-[300px] gold-glow" />
          <h2 className="relative font-display text-3xl sm:text-4xl text-white">{t("home.tagline")}</h2>
          <button onClick={() => go()} className="relative mt-6 px-8 py-3 rounded-full bg-gold text-ink font-semibold hover:brightness-110 transition-all duration-200">{t("common.ask_ai")}</button>
        </div>
      </section>
    </div>
  );
}
